<script setup lang="ts">
import { getParamsNumbers } from '~/utils/getParamsPageResult'
import { useResultStore } from '~/store/result'
import { ID_LOTTO_FACIL } from '~/utils/constants/idsLotteries.js'

defineOptions({
	name: 'AnimationNumbersSelect',
})

const route = useRoute()
const resultStore = useResultStore()
const { t: $t } = useI18n()

type Item = (string | number | null)[]
interface NumbersBlock {
	first: Item | null
	last?: Item | null
}

const listNumbers = ref<NumbersBlock | null>({
	first: null,
	last: null,
})
const visibleFirstCount = ref(0)
const visibleLastCount = ref(0)

const isLotteryBig = computed(() => {
	if (resultStore.isMutipleBets === true) return true
	const lottery = resultStore.lottery as { id?: string | number } | null | undefined
	if (!lottery) return false
	return lottery.id === ID_LOTTO_FACIL
})

const runInterval = (length: number, updateFn: (count: number) => void): Promise<void> => {
	return new Promise((resolve) => {
		let count = 0
		const interval = setInterval(() => {
			if (count < length) {
				updateFn(count + 1)
				count++
			} else {
				clearInterval(interval)
				resolve()
			}
		}, 500)
	})
}

const revealNumbersSequentially = async () => {
	const { first = null, last = null } = listNumbers.value || {}
	const firstBlockLength = first?.length || 0
	const lastBlockLength = last?.length || 0

	if (firstBlockLength > 0) {
		await runInterval(firstBlockLength, (val: number) => (visibleFirstCount.value = val))
	}

	if (lastBlockLength > 0) {
		await runInterval(lastBlockLength, (val: number) => (visibleLastCount.value = val))
	}
	return
}

onMounted(() => {
	const value = getParamsNumbers(route)
	listNumbers.value = value
	revealNumbersSequentially()
})
</script>

<template>
	<section class="bg-success-600 sm:bg-primary-600 flex-1 flex h-screen">
		<div class="container mx-auto">
			<div class="flex justify-center items-center py-10">
				<div
					class="space-y-7"
					:class="[isLotteryBig ? 'max-w-2xl' : 'max-w-lg']"
				>
					<div v-if="listNumbers">
						<div class="flex flex-wrap sm:flex-nowrap items-center gap-y-6 sm:gap-y-0 gap-x-12">
							<!-- Primer bloque de números -->
							<ul class="mx-auto flex flex-wrap justify-center items-center gap-2 smm:gap-2.5">
								<li
									v-for="(num, index) in listNumbers.first"
									:key="index"
									class="h-full"
								>
									<div
										class="size-14 rounded-full font-semibold text-sm text-center border-none bg-neutral-50 flex justify-center items-center text-secondary-900 overflow-hidden"
									>
										<Transition name="bounce-in">
											<span
												v-if="index < visibleFirstCount"
												class="w-full"
											>
												{{ num }}
											</span>
										</Transition>
									</div>
								</li>
								<template v-if="resultStore.isMutipleBets && listNumbers?.last !== null">
									<li
										v-for="(num, index) in listNumbers.last"
										:key="index"
										class="h-full"
									>
										<div
											class="size-14 rounded-full font-semibold text-sm text-center border-none bg-yellow-300 flex justify-center items-center text-secondary-900 overflow-hidden"
										>
											<Transition name="bounce-in">
												<span
													v-if="index < visibleLastCount"
													class="w-full"
												>
													{{ num }}
												</span>
											</Transition>
										</div>
									</li>
								</template>
							</ul>

							<!-- Segundo bloque de números (si existe) -->
							<ul
								v-if="resultStore.isMutipleBets === false && listNumbers?.last !== null"
								class="mx-auto flex flex-wrap smm:flex-nowrap justify-center items-center gap-2 smm:gap-2.5"
							>
								<li
									v-for="(num, index) in listNumbers.last"
									:key="index"
									class="h-full"
								>
									<div
										class="size-14 rounded-full font-semibold text-sm text-center border-none bg-yellow-300 flex justify-center items-center text-secondary-900 overflow-hidden"
									>
										<Transition name="bounce-in">
											<span
												v-if="index < visibleLastCount"
												class="w-full"
											>
												{{ num }}
											</span>
										</Transition>
									</div>
								</li>
							</ul>
						</div>
					</div>
					<div class="space-y-4 text-center text-primary-50">
						<h2 class="font-bold smm:font-semibold text-lg smm:text-xl">
							{{ $t('Spinning the wheel of fortune') }}...
						</h2>
						<p class="text-sm smm:text-lg smm:px-10">
							{{ $t(`We're analyzing 1,000,000+ data points to find your lucky numbers`) }}!
						</p>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped>
.bounce-in-enter-active {
	animation: bounce-in 0.7s;
}
.bounce-in-leave-active {
	animation: bounce-in 0.7s reverse;
}
@keyframes bounce-in {
	0% {
		opacity: 0;
		transform: translateX(-120%);
	}
	40% {
		opacity: 1;
		transform: translateX(40%);
	}
	50% {
		transform: translateX(-10%);
	}
	75% {
		transform: translateX(5%);
	}
	90% {
		transform: translateX(0);
	}
}
</style>
